import { t } from "@travily/i18n";
import { pillTab, pillTabIndicator, pillTabList } from "@ui/components/PillTabs";
import { Link } from "@ui/primitives/Link";

export type ProfileTabKey = "profile" | "store";

export interface ProfileTabHrefs {
  profileHref: string;
  storeHref: string;
}

export interface ProfileTabsProps extends ProfileTabHrefs {
  currentTab: ProfileTabKey;
}

/**
 * Profile/store pill nav for profile pages. Each tab is its own Inertia route
 * (`/@handle` and `/@handle/store`) sharing the profile chrome, so tabs render
 * as prefetching links rather than client-side tab state — the URL is the
 * single source of truth for the active tab.
 */
export function ProfileTabs({ currentTab, profileHref, storeHref }: ProfileTabsProps) {
  const tabs = [
    { key: "profile" as const, href: profileHref, label: t("common.tabs.profile") },
    { key: "store" as const, href: storeHref, label: t("common.tabs.store") },
  ];

  return (
    <nav aria-label={t("components.frames.profile.nav_label")} className={pillTabList()}>
      {tabs.map((tab) => {
        const active = tab.key === currentTab;
        return (
          <Link
            aria-current={active ? "page" : undefined}
            className={pillTab({ size: "lg" })}
            data-selected={active || undefined}
            href={tab.href}
            key={tab.key}
            prefetch="hover"
          >
            <span className="relative z-10">{tab.label}</span>
            {active && <span aria-hidden className={pillTabIndicator()} />}
          </Link>
        );
      })}
    </nav>
  );
}
